import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBasketballSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBocceSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiPurposeSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOtherSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRacquetballSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasShuffleboardSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSportsFieldSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTennisSportCourt,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVolleyballSportCourt,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave } from '../../utils';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';

import { SPORT_COURT_KEYS, SPORT_COURT_METADATA, type SportCourtKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<SportCourtKey, boolean>;

const SportCourtsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const sportCourtTypes = listingDetails?.buildingAttributes?.sportCourtTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBasketball: sportCourtTypes?.hasBasketball ?? false,
    hasBocce: sportCourtTypes?.hasBocce ?? false,
    hasMultiPurpose: sportCourtTypes?.hasMultiPurpose ?? false,
    hasRacquetball: sportCourtTypes?.hasRacquetball ?? false,
    hasShuffleboard: sportCourtTypes?.hasShuffleboard ?? false,
    hasSportsField: sportCourtTypes?.hasSportsField ?? false,
    hasTennis: sportCourtTypes?.hasTennis ?? false,
    hasVolleyball: sportCourtTypes?.hasVolleyball ?? false,
    hasOther: sportCourtTypes?.hasOther ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Sport courts updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateBasketball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBasketballSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBasketballSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateBocce } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBocceSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBocceSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMultiPurpose } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiPurposeSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiPurposeSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateRacquetball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRacquetballSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRacquetballSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateShuffleboard } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasShuffleboardSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasShuffleboardSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSportsField } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSportsFieldSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSportsFieldSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateTennis } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTennisSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTennisSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateVolleyball } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVolleyballSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVolleyballSportCourt',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOther } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOtherSportCourt,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOtherSportCourt',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasBasketball: updateBasketball,
    hasBocce: updateBocce,
    hasMultiPurpose: updateMultiPurpose,
    hasRacquetball: updateRacquetball,
    hasShuffleboard: updateShuffleboard,
    hasSportsField: updateSportsField,
    hasTennis: updateTennis,
    hasVolleyball: updateVolleyball,
    hasOther: updateOther,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as SportCourtKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = SPORT_COURT_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Sport courts</Legend>} disabled={isInputFormDisabled}>
      {SPORT_COURT_KEYS.map((fieldName) => {
        const metadata = SPORT_COURT_METADATA[fieldName];
        return (
          <LabeledControl
            key={`sportCourtTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(SportCourtsAmenities);
